import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import http from '../../services/http';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { TIPOS_ACEITOS, prepararImagem } from '../../utils/imagemDaAssinatura';

/**
 * A assinatura do operador, editada pelo próprio operador.
 *
 * ## Por que uma seção própria, e não um campo no diálogo "Edição de Dados"
 *
 * Porque a api tem um endpoint PRÓPRIO para ela (`PUT
 * /users/{id}/assinatura`), e o diálogo salva pelo update genérico do usuário.
 * Enfiar o campo lá obrigaria aquele fluxo a fazer duas chamadas e a decidir o
 * que fazer quando uma passa e a outra falha -- num formulário que hoje
 * funciona. Seção separada, salvamento separado, nada do que existe muda.
 *
 * ## O limite aparece antes de incomodar
 *
 * 1000 caracteres é o teto da api. Um contador que só aparece perto do fim
 * avisa sem poluir: assinatura de verdade tem três linhas, e quem está nesse
 * caso nunca vê o contador.
 */
export const LIMITE_DA_ASSINATURA = 1000;

const AssinaturaDoOperador = ({ usuario, onSalvou }) => {
    const { theme } = useContext(ThemeContext);
    const [texto, setTexto] = useState('');
    const [salvando, setSalvando] = useState(false);
    /*
     * A IMAGEM (Gisele, 19/10/2026). Três estados possíveis, e é por isso que
     * ela não é um booleano:
     *
     * - `undefined`: o operador não mexeu. A api não recebe o campo, e a
     *   imagem que ele já tem fica como está.
     * - uma data URL: escolheu uma nova.
     * - `null`: pediu para remover.
     */
    const [imagemNova, setImagemNova] = useState(undefined);
    const arquivoRef = useRef(null);

    // A assinatura chega junto do usuário, que carrega depois da primeira
    // pintura. Sem este efeito a caixa ficaria vazia para quem já tem uma
    // assinatura salva -- e salvar por cima a APAGARIA.
    useEffect(() => {
        setTexto(usuario?.assinatura || '');
        setImagemNova(undefined);
    }, [usuario?.id, usuario?.assinatura, usuario?.assinatura_imagem]);

    const original = usuario?.assinatura || '';
    const imagemAtual = usuario?.assinatura_imagem || null;
    // O que a prévia mostra: a escolhida agora, ou a que já estava salva.
    const previa = imagemNova === undefined ? imagemAtual : imagemNova;
    const mudou = texto !== original || imagemNova !== undefined;
    const estourou = texto.length > LIMITE_DA_ASSINATURA;

    const escolherArquivo = async (evento) => {
        const arquivo = evento.target.files?.[0];

        // O input é limpo SEMPRE: sem isso, escolher o mesmo arquivo de novo
        // depois de remover não dispara `change`, e a tela parece travada.
        evento.target.value = '';

        if (!arquivo) return;

        try {
            setImagemNova(await prepararImagem(arquivo));
        } catch (err) {
            toast.error(err?.message || 'Não foi possível usar esta imagem.');
        }
    };

    const salvar = async () => {
        if (!usuario?.id || estourou) return;

        setSalvando(true);
        try {
            const corpo = { assinatura: texto };

            // Só manda o campo da imagem se ele mudou: o `sometimes` da api
            // existe para que salvar o texto não apague a imagem de quem já
            // tem uma.
            if (imagemNova !== undefined) corpo.assinatura_imagem = imagemNova;

            await http.put(`/users/${usuario.id}/assinatura`, corpo);
            toast.success(
                (texto.trim() || previa) ? 'Assinatura salva.' : 'Assinatura removida.'
            );
            setImagemNova(undefined);
            if (onSalvou) onSalvou();
        } catch (err) {
            toast.error(
                err?.response?.data?.message
                || 'Não foi possível salvar a assinatura.'
            );
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 420 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Entra no fim das ações que você escrever no chamado. Você pode desmarcar
                na hora, em cada ação.
            </Typography>
            <TextField
                multiline
                minRows={3}
                maxRows={8}
                size="small"
                placeholder={'Seu nome\nCargo\nTelefone'}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                error={estourou}
                inputProps={{ 'aria-label': 'Minha assinatura' }}
            />
            {/*
              * A imagem fica DEPOIS da caixa de texto, na mesma ordem em que
              * aparece na ação: nome, cargo, telefone, e então o logo.
              */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    disabled={salvando}
                    sx={{ textTransform: 'none' }}
                >
                    {previa ? 'Trocar imagem' : 'Adicionar imagem'}
                    <input
                        hidden
                        ref={arquivoRef}
                        type="file"
                        accept={TIPOS_ACEITOS.join(',')}
                        aria-label="Imagem da assinatura"
                        onChange={escolherArquivo}
                    />
                </Button>
                {previa && (
                    <Button
                        size="small"
                        color="error"
                        disabled={salvando}
                        onClick={() => setImagemNova(null)}
                        sx={{ textTransform: 'none' }}
                    >
                        Remover imagem
                    </Button>
                )}
            </Box>
            {previa && (
                <Box
                    sx={{
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        // Fundo neutro: o logo costuma ter fundo transparente,
                        // e no tema escuro um logo preto sumiria.
                        backgroundColor: theme.palette.background.paper,
                        alignSelf: 'flex-start',
                    }}
                >
                    <img
                        src={previa}
                        alt="Prévia da assinatura"
                        style={{ maxWidth: 300, maxHeight: 120, display: 'block' }}
                    />
                </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                    size="small"
                    variant="contained"
                    disabled={!mudou || salvando || estourou}
                    onClick={salvar}
                    sx={{ textTransform: 'none' }}
                >
                    {salvando ? 'Salvando...' : 'Salvar assinatura'}
                </Button>
                {/*
                  * O contador só aparece quando passa de 80% do teto: antes
                  * disso é informação que ninguém pediu, e assinatura de
                  * verdade nunca chega perto.
                  */}
                {texto.length > LIMITE_DA_ASSINATURA * 0.8 && (
                    <Typography
                        variant="caption"
                        sx={{ color: estourou ? theme.palette.error.main : theme.palette.text.secondary }}
                    >
                        {texto.length} / {LIMITE_DA_ASSINATURA}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default AssinaturaDoOperador;
